/**
 * /best-of/:category — programmatic SEO landing page.
 *
 * Top 5-10 apps in a feature-category, each with a clickable AI verdict
 * snippet vs the category's #1 app. Pure read-only — verdicts are pulled
 * from cache so no LLM cost on this route. Falls back to "Compare →" CTA
 * when a snippet hasn't been generated yet.
 */
import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { Star, Trophy, Sparkles, ArrowRight, ChevronRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { mediaUrl } from "@/lib/api";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANK_STYLES = {
  1: { ring: "ring-2 ring-amber-400", badge: "bg-amber-500", label: "#1" },
  2: { ring: "ring-1 ring-slate-300", badge: "bg-slate-400", label: "#2" },
  3: { ring: "ring-1 ring-orange-300", badge: "bg-orange-500", label: "#3" },
};

const BestOfCategoryPage = () => {
  const { category } = useParams();
  const { lang } = useLang();
  const isNl = lang === "nl";
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/best-of/${category}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (!cancelled) setErr(e?.response?.status === 404 ? "not-found" : "error");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [category]);

  const label = data ? (isNl ? data.label_nl : data.label_en) : (category || "");
  const title = data
    ? (isNl
      ? `Beste ${label} paardenapps in 2026 — Top ${data.items.length}`
      : `Best ${label} Horse Apps 2026 — Top ${data.items.length}`)
    : `Best ${label}`;
  const description = data
    ? (isNl
      ? `De top ${data.items.length} paardenapps in ${label} in 2026, met AI-vergelijking en directe links naar uitgebreide vergelijkingen.`
      : `The top ${data.items.length} horse apps for ${label} in 2026, with AI-powered comparisons and side-by-side analysis.`)
    : "";

  const url = `${typeof window !== "undefined" ? window.location.origin : ""}/best-of/${category}`;

  // Inject SEO meta tags directly (no react-helmet dep in this codebase).
  useEffect(() => {
    if (!data) return;
    const prevTitle = document.title;
    document.title = `${title} · HorseAppCompare`;
    const ensure = (sel, create) => {
      let el = document.head.querySelector(sel);
      if (!el) { el = create(); document.head.appendChild(el); }
      return el;
    };
    const desc = ensure('meta[name="description"]', () => { const m = document.createElement("meta"); m.setAttribute("name", "description"); return m; });
    desc.setAttribute("content", description);
    const canon = ensure('link[rel="canonical"]', () => { const l = document.createElement("link"); l.setAttribute("rel", "canonical"); return l; });
    canon.setAttribute("href", url);
    return () => { document.title = prevTitle; };
  }, [data, title, description, url]);

  const ldJson = useMemo(() => {
    if (!data?.items?.length) return null;
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListOrder: "https://schema.org/ItemListOrderDescending",
      numberOfItems: data.items.length,
      name: title,
      url,
      itemListElement: data.items.map((it) => ({
        "@type": "ListItem",
        position: it.rank,
        name: it.name,
        url: `/apps/${it.slug}`,
      })),
    };
  }, [data, title, url]);

  if (loading) {
    return (
      <main className="hac-container py-20 text-center text-[#94A3B8]">
        {isNl ? "Laden…" : "Loading…"}
      </main>
    );
  }
  if (err === "not-found") {
    return (
      <main className="hac-container py-20">
        <h1 className="font-heading text-3xl text-[#0F172A] mb-3">{isNl ? "Categorie niet gevonden" : "Category not found"}</h1>
        <p className="text-[#64748B] mb-4">{isNl ? "Deze categorie bestaat (nog) niet of heeft geen apps." : "This category doesn't exist yet or has no listed apps."}</p>
        <Link to="/categories" className="text-[#2563EB] hover:underline">{isNl ? "← Alle categorieën" : "← All categories"}</Link>
      </main>
    );
  }
  if (err) {
    return (
      <main className="hac-container py-20 text-center text-red-600">
        {isNl ? "Er ging iets mis. Probeer opnieuw." : "Something went wrong. Please try again."}
      </main>
    );
  }
  if (!data || data.items.length === 0) return null;

  return (
    <main data-testid="best-of-page">
      {ldJson && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson) }} />
      )}

      {/* Hero */}
      <section className="border-b border-[#E2E8F0] bg-gradient-to-br from-[#FFFBEB] via-white to-[#FEF2F2]">
        <div className="hac-container py-12 md:py-16">
          <nav className="text-xs text-[#94A3B8] mb-4 flex items-center gap-2">
            <Link to="/" className="hover:text-[#2563EB]">Home</Link>
            <ChevronRight size={12} />
            <Link to="/categories" className="hover:text-[#2563EB]">{isNl ? "Categorieën" : "Categories"}</Link>
            <ChevronRight size={12} />
            <span className="text-[#0F172A] font-medium">{isNl ? "Beste" : "Best of"} {label}</span>
          </nav>
          <div className="overline mb-3 text-amber-700 inline-flex items-center gap-1.5"><Trophy size={12} /> {isNl ? "Top picks" : "Top picks"} 2026</div>
          <h1 className="font-heading text-4xl md:text-6xl text-[#0F172A] leading-[1.05] mb-5">
            {isNl ? "Beste" : "Best"} {label} {isNl ? "paardenapps" : "horse apps"}
          </h1>
          <p className="text-lg text-[#64748B] leading-relaxed max-w-3xl">
            {isNl
              ? `${data.items.length} winnaars uit ${data.total_in_category} apps die we hebben getest in deze categorie, beoordeeld op functies, prijs, AI en gebruikerservaring.`
              : `${data.items.length} winners out of ${data.total_in_category} apps tested in this category, scored on features, pricing, AI capabilities and user experience.`}
          </p>
        </div>
      </section>

      {/* Ranked list */}
      <section className="hac-container py-12 max-w-5xl">
        <ul className="space-y-6">
          {data.items.map((it) => {
            const style = RANK_STYLES[it.rank] || { ring: "", badge: "bg-slate-500", label: `#${it.rank}` };
            return (
              <li
                key={it.slug}
                className={`relative bg-white border border-[#E2E8F0] rounded-2xl p-5 md:p-6 ${style.ring}`}
                data-testid={`best-of-card-${it.slug}`}
              >
                <div className="absolute -top-3 -left-3 z-10">
                  <div className={`${style.badge} text-white text-sm font-bold px-3 py-1 rounded-full shadow-md`}>
                    {style.label}
                  </div>
                </div>

                <div className="flex flex-col md:flex-row gap-5">
                  {/* Logo + score */}
                  <div className="md:w-32 flex md:flex-col items-center md:items-stretch gap-3 shrink-0">
                    <div className="w-16 h-16 md:w-24 md:h-24 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] flex items-center justify-center overflow-hidden shrink-0">
                      {it.logo ? (
                        <img src={mediaUrl(it.logo)} alt={`${it.name} logo`} className="w-full h-full object-cover" />
                      ) : (
                        <div className="font-heading text-2xl text-[#94A3B8]">{it.name.charAt(0)}</div>
                      )}
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-[#94A3B8] uppercase tracking-widest font-bold">Score</div>
                      <div className="font-heading text-2xl text-[#0F172A] tabular-nums leading-tight">{Number(it.score).toFixed(1)}</div>
                      {it.rating > 0 && (
                        <div className="text-[11px] text-[#64748B] inline-flex items-center gap-1 font-mono mt-0.5">
                          <Star size={10} className="fill-amber-400 text-amber-400" /> {it.rating.toFixed(1)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Main */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2 mb-1">
                      <Link to={`/apps/${it.slug}`} className="font-heading text-2xl md:text-3xl text-[#0F172A] hover:text-[#2563EB] truncate">
                        {it.name}
                      </Link>
                      {it.country && <span className="text-[11px] text-[#94A3B8] shrink-0">{it.country}</span>}
                    </div>
                    {it.tagline && <p className="text-sm text-[#475569] mb-3">{it.tagline}</p>}

                    {/* AI verdict snippet */}
                    {it.snippet && it.snippet.my_verdict && (
                      <div className="mt-2 bg-gradient-to-br from-[#EFF6FF] to-[#F5F3FF] border border-[#DBEAFE] rounded-lg p-3" data-testid={`best-of-snippet-${it.slug}`}>
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Sparkles size={11} className="text-[#7C3AED]" />
                          <span className="text-[10px] uppercase tracking-widest font-bold text-[#7C3AED]">
                            {isNl ? "AI verdict" : "AI verdict"} vs {data.items.find((x) => x.slug === it.snippet.vs_slug)?.name || it.snippet.vs_slug}
                          </span>
                        </div>
                        <p className="text-sm text-[#1E293B] leading-relaxed mb-2">{it.snippet.my_verdict}</p>
                        <Link
                          to={it.snippet.compare_url}
                          className="text-xs font-bold text-[#2563EB] hover:text-[#1D4ED8] inline-flex items-center gap-1"
                          data-testid={`best-of-compare-link-${it.slug}`}
                        >
                          {isNl ? "Volledige vergelijking" : "Full comparison"} <ArrowRight size={11} />
                        </Link>
                      </div>
                    )}

                    {/* Fallback CTA when no verdict cached yet */}
                    {!it.snippet?.my_verdict && data.items[0]?.slug !== it.slug && (
                      <Link
                        to={`/compare/${data.items[0].slug}-vs-${it.slug}`}
                        className="text-xs font-bold text-[#2563EB] hover:text-[#1D4ED8] inline-flex items-center gap-1 mt-2"
                      >
                        {isNl ? `Vergelijk met ${data.items[0].name}` : `Compare with ${data.items[0].name}`} <ArrowRight size={11} />
                      </Link>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {/* Footer CTA */}
      <section className="hac-container pb-20 max-w-5xl">
        <div className="bg-[#0F172A] text-white rounded-2xl p-8 md:p-10 text-center">
          <h2 className="font-heading text-2xl md:text-3xl mb-3">
            {isNl ? "Niet de juiste in deze lijst?" : "None of these quite right?"}
          </h2>
          <p className="text-[#94A3B8] mb-5 max-w-xl mx-auto">
            {isNl
              ? "Onze AI Advisor stelt je 5 vragen en kiest dé app voor jou uit alle 25+ apps in onze database."
              : "Our AI Advisor asks you 5 quick questions and picks the perfect app from all 25+ apps in our database."}
          </p>
          <Link to="/advisor" className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] px-6 py-3 rounded-full font-bold transition-colors">
            <Sparkles size={16} /> {isNl ? "Vraag de adviseur" : "Ask the advisor"}
          </Link>
        </div>
      </section>
    </main>
  );
};

export default BestOfCategoryPage;
